import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toastService } from '../services/toast';
import { CreditCardIcon, CheckCircleIcon, ClockIcon, XCircleIcon } from '@heroicons/react/outline';
import { ordersApi } from '../services/api/orders.api';

// مكون Skeleton لصفحة المدفوعات
export const PaymentsSkeleton = () => {
    return (
        <div className="space-y-8">
            {/* سجل المدفوعات */}
            <div>
                <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg">
                    <div className="prose dark:prose-invert max-w-none text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700/50 p-3 rounded-lg">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <div className="h-6 w-6 bg-gray-200 dark:bg-gray-600 rounded-full ml-2"></div>
                                <div className="h-5 w-28 bg-gray-200 dark:bg-gray-600 rounded"></div>
                            </div>
                        </div>
                    </div>
                    <div className="p-0 sm:p-6 mt-4">
                        <div className="space-y-4">
                            {[...Array(5)].map((_, i) => (
                                <div key={`payment-skeleton-${i}`} className="animate-pulse bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="h-5 bg-gray-200 dark:bg-gray-600 rounded mb-3 w-40"></div>
                                            <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-28"></div>
                                        </div>
                                        <div className="text-left">
                                            <div className="h-6 bg-gray-200 dark:bg-gray-600 rounded mb-2 w-20"></div>
                                            <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-14"></div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default function Payments() {
    const navigate = useNavigate();
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchPaymentHistory();
    }, []);

    const fetchPaymentHistory = async () => {
        try {
            setLoading(true);

            // جلب طلبات الكتب الخاصة بالمستخدم فقط
            const bookResponse = await ordersApi.getUserOrders('book');
            // Process book orders response
            
            // جلب طلبات الدورات الخاصة بالمستخدم فقط
            const courseResponse = await ordersApi.getUserOrders('course');
            // Process course orders response
            
            // تحضير مدفوعات الكتب
            const bookPayments = bookResponse.map(order => {
                let methodTitle;

                // إذا كان المبلغ صفر، فهو مجاني
                if (Number(order.amount) === 0 || Number(order.final_total) === 0) {
                    methodTitle = 'مجاني';
                }
                // التحقق من نوع الدفع
                else if (order.payment_method === 'bank-transfer') {
                    methodTitle = 'تحويل بنكي';
                }
                else if (order.payment_method === 'electronic') {
                    methodTitle = 'دفع إلكتروني';
                }
                else if (order.payment_method === 'installment') {
                    methodTitle = 'أقساط';
                }
                else {
                    methodTitle = order.payment_method || 'غير محدد';
                }
                
                return {
                    id: order.id,
                    title: order.items?.map(item => item.bookTitle).join('، '),
                    amount: order.amount,
                    date: order.created_at,
                    status: order.status,
                    method: order.payment_method,
                    methodTitle,
                    type: 'book'
                };
            });
            
            // تحضير مدفوعات الدورات
            const coursePayments = courseResponse.map(order => {
                let methodTitle;

                // إذا كان المبلغ صفر، فهو مجاني
                if (Number(order.amount) === 0 || Number(order.final_total) === 0) {
                    methodTitle = 'مجاني';
                }
                // التحقق من نوع الدفع
                else if (order.payment_method === 'bank-transfer') {
                    methodTitle = 'تحويل بنكي';
                }
                else if (order.payment_method === 'electronic') {
                    methodTitle = 'دفع إلكتروني';
                }
                else if (order.payment_method === 'installment') {
                    methodTitle = 'أقساط';
                }
                else {
                    methodTitle = order.payment_method || 'غير محدد';
                }
                
                return {
                    id: order.id,
                    title: order.items?.map(item => item.courseTitle).join('، '),
                    amount: order.amount,
                    date: order.created_at,
                    status: order.status,
                    method: order.payment_method,
                    methodTitle,
                    type: 'course'
                };
            });

            // دمج مدفوعات الكتب والدورات
            const allPayments = [...bookPayments, ...coursePayments];
            
            // ترتيب المدفوعات حسب التاريخ (الأحدث أولاً)
            allPayments.sort((a, b) => new Date(b.date) - new Date(a.date));

            setPayments(allPayments);
        } catch (error) {
            // Handle payment history fetch error
            toastService.error('حدث خطأ في تحميل سجل المدفوعات');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('ar-EG', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getStatusInfo = (status, methodTitle) => {
        let icon, className, text;

        // الكتب المجانية
        if (methodTitle === 'مجاني') {
            icon = <CheckCircleIcon className="h-4 w-4 ml-1" />;
            className = 'text-green-600 dark:text-green-400';
            text = 'مقبول';
        }
        // التحويل البنكي
        else if (methodTitle === 'تحويل بنكي') {
            if (status === 'pending') {
                icon = <ClockIcon className="h-4 w-4 ml-1" />;
                className = 'text-yellow-600 dark:text-yellow-400';
                text = 'تحت المراجعة';
            } else if (status === 'accepted') {
                icon = <CheckCircleIcon className="h-4 w-4 ml-1" />;
                className = 'text-green-600 dark:text-green-400';
                text = 'مقبول';
            } else if (status === 'rejected') {
                icon = <XCircleIcon className="h-4 w-4 ml-1" />;
                className = 'text-red-600 dark:text-red-400';
                text = 'مرفوض';
            }
        }
        // الدفع الإلكتروني - يا مقبول يا مرفوض فقط
        else if (methodTitle === 'دفع إلكتروني') {
            if (status === 'accepted') {
                icon = <CheckCircleIcon className="h-4 w-4 ml-1" />;
                className = 'text-green-600 dark:text-green-400';
                text = 'مقبول';
            } else {
                // أي حالة تانية تبقى مرفوضة
                icon = <XCircleIcon className="h-4 w-4 ml-1" />;
                className = 'text-red-600 dark:text-red-400';
                text = 'مرفوض';
            }
        }
        // الدفع بتمارا
        else if (methodTitle === 'tamara') {
            if (status === 'accepted' || status === 'completed') {
                icon = <CheckCircleIcon className="h-4 w-4 ml-1" />;
                className = 'text-green-600 dark:text-green-400';
                text = 'مقبول';
            } else {
                // أي حالة تانية تبقى مرفوضة
                icon = <XCircleIcon className="h-4 w-4 ml-1" />;
                className = 'text-red-600 dark:text-red-400';
                text = 'مرفوض';
            }
        }

        // تقسيط
        else if (methodTitle === 'أقساط') {
            if (status === 'accepted' || status === 'completed') {
                icon = <CheckCircleIcon className="h-4 w-4 ml-1" />;
                className = 'text-green-600 dark:text-green-400';
                text = 'جاري';
            } else {
                icon = <XCircleIcon className="h-4 w-4 ml-1" />;
                className = 'text-red-600 dark:text-red-400';
                text = 'مرفوض';
            }
        }

        // لو مفيش أيقونة (يعني الحالة غير معروفة)
        if (!icon) {
            icon = <XCircleIcon className="h-4 w-4 ml-1" />;
            className = 'text-red-600 dark:text-red-400';
            text = 'مرفوض';
        }

        return {
            icon,
            text,
            className
        };
    }

    if (loading) {
        return <PaymentsSkeleton />;
    }

    return (
        <div className="space-y-8">
            {/* سجل المدفوعات */}
            <div>
                <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg">
                    <div className="prose dark:prose-invert max-w-none text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700/50 p-3 rounded-lg">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <CreditCardIcon className="h-6 w-6 text-gray-500 dark:text-gray-400 ml-2" />
                                <h2 className="text-lg font-medium text-gray-900 dark:text-white">سجل المدفوعات</h2>
                            </div>
                        </div>
                    </div>
                    <div className="p-0 sm:p-6 mt-4">
                        {payments.length > 0 ? (
                            <div className="space-y-4">
                                {payments.map((payment) => {
                                    return (
                                        <div key={payment.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                                            onClick={() => payment.method === 'installment' ? navigate(`/installment-details/${payment.id}`) : navigate(`/order-confirmation/${payment.id}`)}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h3 className="text-base font-medium text-gray-900 dark:text-white mb-1">
                                                        {payment.title}
                                                    </h3>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                                        طريقة الدفع: {payment.methodTitle}
                                                    </p>
                                                </div>
                                                <div className="text-left">
                                                    <div className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                                                        {payment.amount} ريال
                                                    </div>
                                                    <div className="flex items-center">
                                                        {(() => {
                                                            const statusInfo = getStatusInfo(payment.status, payment.methodTitle);
                                                            if (statusInfo) {
                                                                return (
                                                                    <div className={`flex items-center ${statusInfo.className}`}>
                                                                        {statusInfo.icon}
                                                                        <span>{statusInfo.text}</span>
                                                                    </div>
                                                                );
                                                            } else {
                                                                return (
                                                                    <div className="flex items-center text-red-600 dark:text-red-400">
                                                                        <XCircleIcon className="h-4 w-4 ml-1" />
                                                                        <span>فشلت</span>
                                                                    </div>
                                                                );
                                                            }
                                                        })()}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <CreditCardIcon className="mx-auto h-12 w-12 text-gray-400" />
                                <h3 className="mt-2 text-base font-medium text-gray-900 dark:text-white">لا توجد مدفوعات</h3>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">لم تقم بأي عمليات شراء حتى الآن</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
